<script setup lang="ts">
import { useFamilyStore } from "@/stores/familyStore";
import { useI18n } from "vue-i18n";
import AddOutlineButton from "@/components/buttons/AddOutlineButton.vue";
import FamilyMemberModal from "@/views/Auth/components/FamilyMemberModal.vue";
import { onMounted, ref } from "vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import PencilIcon from "@/components/icons/PencilIcon.vue";
import { FamilyMember } from "@/types/familyTypes";
import { useToastNotificationStore } from "@/stores/toastNotificationStore";
import { useRoute } from "vue-router";
import router from "@/router";

const familyStore = useFamilyStore();
const toastStore = useToastNotificationStore();
const { t } = useI18n();
const route = useRoute();

const showAddModal = ref(false);
const editableFamilyMember = ref<FamilyMember>();
const waitApiCall = ref(false);

onMounted(() => {
  familyStore.loadIfNeeded();

  if (route.query.openFamilyModal === "true") {
    showAddModal.value = true;
    router.replace({ query: { ...route.query, openFamilyModal: undefined } });
  }
});

function handleCloseModal() {
  showAddModal.value = false;
  editableFamilyMember.value = undefined;
}

function handleUpdateFamilyMember(familyMember: FamilyMember) {
  editableFamilyMember.value = familyMember;
  showAddModal.value = true;
}

async function handleDeleteFamilyMember(familyMember: FamilyMember) {
  if (waitApiCall.value) {
    return;
  }
  try {
    waitApiCall.value = true;
    await familyStore.deleteFamilyMember(familyMember);
  } catch (e) {
    await toastStore.createNotificationsFromApi(e);
    console.log(e);
  } finally {
    waitApiCall.value = false;
  }
}

function formatDate(rawDate: string) {
  const date = new Date(rawDate);

  return date
    .toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    .replace(/\./g, "-");
}
</script>

<template>
  <div class="flex mt-6 mb:mt-10 justify-center">
    <div class="flex-1 max-w-[812px] bg-white rounded-[20px] pt-[30px]">
      <!-- TITLE -->
      <div
        class="flex justify-start items-center md:justify-center mb-6 md:mb-10 relative"
      >
        <p
          class="sf-pro font-semibold text-xl max-md:leading-[24px] md:text-2xl max-md:pl-4"
        >
          {{ t("family-table.title") }}
        </p>
        <AddOutlineButton
          v-if="familyStore.familyMembers.length < 4"
          class="absolute right-4"
          @click="showAddModal = true"
        />
      </div>
      <!-- FAMILY TABLE -->
      <div
        class="rounded-[20px] overflow-y-auto"
        v-if="familyStore.familyMembers.length > 0"
      >
        <table class="w-full text-left overflow-hidden" ref="tableRef">
          <thead class="bg-brown max-md:hidden">
            <tr>
              <th class="th-item pl-4">
                {{ t("family-table.member.name") }}
              </th>
              <th class="th-item">
                {{ t("family-table.member.gender") }}
              </th>
              <th class="th-item">
                {{ t("family-table.member.city") }}
              </th>
              <th class="th-item">
                {{ t("family-table.member.birthdate") }}
              </th>
              <th class="th-item">
                {{ t("family-table.member.actions") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="familyMember in familyStore.familyMembers"
              :key="familyMember.id"
              class="h-20 border-b border-ternary last:border-b-0"
            >
              <td class="pl-4">{{ familyMember.name }}</td>
              <td class="max-md:hidden">
                {{ t(`family-table.gender.${familyMember.gender}`) }}
              </td>
              <td class="max-md:hidden">
                {{ familyMember.city }}
              </td>
              <td class="max-md:hidden">
                {{ formatDate(familyMember.birthdate) }}
              </td>
              <td>
                <div
                  class="flex items-center gap-4 max-md:pr-4 max-md:justify-end"
                >
                  <button
                    class="size-10 border border-muted rounded-lg"
                    :class="{
                      'opacity-40 pointer-events-none': waitApiCall,
                    }"
                    @click="handleDeleteFamilyMember(familyMember)"
                  >
                    <CloseIcon class="mx-auto" />
                  </button>
                  <button
                    class="size-10 border border-muted rounded-lg"
                    :class="{
                      'opacity-40 pointer-events-none': waitApiCall,
                    }"
                    @click="handleUpdateFamilyMember(familyMember)"
                  >
                    <PencilIcon class="mx-auto" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <FamilyMemberModal
      :show="showAddModal"
      :editableFamilyMember="editableFamilyMember"
      @close="handleCloseModal"
    />
  </div>
</template>

<style scoped>
.th-item {
  @apply text-white font-semibold text-xl leading-[24px] py-9;
}
</style>
